import { memo } from "react"
import { IconArrowUpRightOutline18 as External } from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/cn"
import { getPortalUrl } from "@productlane/lib/src/domain"
import { Button, Loader, Switch } from "@productlane/ui"
import { ApiReferencePageView } from "@productlane/ui/src/components/api-reference"

import { RoleCheck } from "@/components/shared/role-check"
import { toast } from "@/components/toast"
import { api } from "@/lib/api"
import { useZeroWorkspace } from "@/lib/zero"
import { MobileBackButton } from "../mobile-back-button"
import { MOBILE_BAR_CLEARANCE } from "../sidebar-geometry"
import {
	API_PANE_HEADER_CLASS,
	ApiReferenceDashboardEnv,
} from "./api-reference-env"
import { DocsHeaderTabs } from "./docs-header-tabs"
import { useDocsTabs } from "./use-docs-tabs"

/** One generated page as readers see it. Synced from the spec, so only its visibility changes here. */
export const ApiReferencePreview = memo(
	({
		tabId,
		groupSlug,
		slug,
	}: {
		tabId: string
		groupSlug: string
		slug: string
	}) => {
		const workspace = useZeroWorkspace()
		const { tabs } = useDocsTabs()
		const utils = api.useUtils()
		const page = api.apiReference.page.useQuery({ tabId, groupSlug, slug })
		const reference = api.apiReference.get.useQuery({ tabId })
		const update = api.apiReference.update.useMutation({
			onSuccess: () => utils.apiReference.get.invalidate({ tabId }),
			onError: (error) =>
				toast.error({ title: "Couldn't save", description: error.message }),
		})

		const operation = page.data?.operation
		const hidden = reference.data?.state?.hidden ?? []
		const isVisible = operation ? !hidden.includes(operation.key) : true
		const portalSlug =
			workspace?.portalInstances?.find(
				(p) => p.id === tabs.find((t) => t.id === tabId)?.portalInstanceId,
			)?.slug ?? null
		const liveUrl =
			workspace && page.data && isVisible
				? getPortalUrl(
						workspace,
						`/docs/${page.data.tab.slug}/${groupSlug}/${slug}`,
						portalSlug,
					)
				: null

		return (
			<div className="flex h-full flex-col">
				<div className={cn(API_PANE_HEADER_CLASS, "gap-3")}>
					<DocsHeaderTabs />
					<div className="mr-auto sm:hidden">
						<MobileBackButton
							to={`/edit-docs/api/${tabId}`}
							label="Back to the API reference"
						/>
					</div>
					{operation && (
						<RoleCheck not={["VIEWER"]}>
							<Switch
								name="api-page-visible"
								label="Show in docs"
								direction="text-first"
								checked={isVisible}
								disabled={update.isLoading}
								onCheckedChange={(visible) => {
									const next = visible
										? hidden.filter((key) => key !== operation.key)
										: [...hidden, operation.key]
									update.mutate({ tabId, hidden: next })
								}}
							/>
						</RoleCheck>
					)}
					{liveUrl && (
						<Button
							variant="outline"
							size="sm"
							Icon={External}
							onClick={() => window.open(liveUrl, "_blank", "noopener")}
						>
							View live
						</Button>
					)}
				</div>
				<div className="min-h-0 flex-1 overflow-y-auto">
					{page.isLoading ? (
						<div className="flex h-full items-center justify-center">
							<Loader size={10} />
						</div>
					) : !page.data?.operation ? (
						<p className="p-8 text-sm text-tertiary">
							{"This page isn't in the published spec anymore."}
						</p>
					) : (
						<ApiReferenceDashboardEnv tabId={tabId}>
							<div className={MOBILE_BAR_CLEARANCE}>
								<ApiReferencePageView
									page={page.data}
									className="px-6 sm:px-4 lg:px-8"
								/>
							</div>
						</ApiReferenceDashboardEnv>
					)}
				</div>
			</div>
		)
	},
)
